import { infiniteQueryOptions, keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { captainBillingKeys } from "./query-keys";
import { captainBillingUrl } from "./url";
import { HISTORY_SOURCES } from "../constants";
import type { CaptainBillingHistoryFilters, CaptainBillingHistoryResponse } from "../types";

import { instance } from "@/lib/axios/instance";

export const captainBillingHistoryOptions = (clientId: string, filters: Omit<CaptainBillingHistoryFilters, "page">) =>
  infiniteQueryOptions({
    queryKey: captainBillingKeys.history(clientId, filters),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      instance
        .get<CaptainBillingHistoryResponse>(captainBillingUrl(clientId, "History"), {
          params: { ...filters, page: pageParam },
        })
        .then((res) => res.data),
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((total, page) => total + page.data.length, 0);
      return fetched < lastPage.totalRecords ? allPages.length + 1 : undefined;
    },
    enabled: !!clientId,
    staleTime: 30_000,
  });

export function usePrefetchCaptainBillingHistory(
  clientId: string,
  { source, from, to, pageSize, search, actors, models }: Omit<CaptainBillingHistoryFilters, "page" | "changeTypes">,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const actorKey = actors?.join(",") ?? "";
  const modelKey = models?.join(",") ?? "";
  useEffect(() => {
    if (!enabled || !clientId) return;
    const actorList = actorKey ? actorKey.split(",") : undefined;
    const modelList = modelKey ? modelKey.split(",") : undefined;
    [undefined, ...HISTORY_SOURCES]
      .filter((other) => other !== source)
      .forEach((other) =>
        queryClient.prefetchInfiniteQuery(
          captainBillingHistoryOptions(clientId, { source: other, from, to, pageSize, search, actors: actorList, models: modelList }),
        ),
      );
  }, [queryClient, clientId, source, from, to, pageSize, search, actorKey, modelKey, enabled]);
}

export function useCaptainBillingHistory(clientId: string, filters: Omit<CaptainBillingHistoryFilters, "page">) {
  return useInfiniteQuery({
    ...captainBillingHistoryOptions(clientId, filters),
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });
}
